import styled from '@emotion/styled';
import { Button, Divider, Grid, MenuItem, Typography } from '@material-ui/core';
import { FC, Fragment, useEffect, useState } from 'react';
import { COLOR_TEXT_MAIN } from '../../../../../theme/colors';
import checkUrl from '../../../../../utility/checkUrl';
import StyledInputField from '../../../primitive/StyledInputField';
import StyledIconButton from '../StyledIconButton';
import StyledSelect from '../StyledSelect';
import CloseIcon from '@material-ui/icons/Close';
import ButtonGrid from '../ButtonGrid';
import getComparatorType from '../../../../../utility/getComparatorType';
import EventMatcher from '../../../../../types/EventMatcher';
import InputFieldLabel from '../InputFieldLabel';
import FilterMethod from '../../../../../types/FilterMethod';
import FilterType from '../../../../../types/FilterType';
import MatchEvent from '../../../../../types/MatchEvent';
import Config from '../../../../../core/Config';
import { ConversionGoal } from '../../../../../generated/graphql';
import Condition from '../../../../../types/Condition';

type Props = {
  siteViewerUrl: string;
  goal: ConversionGoal;
  setSiteViewerUrl: (value: React.SetStateAction<string>) => void;
  eventMatchers: EventMatcher[];
  setEventMatchers: (value: React.SetStateAction<EventMatcher[]>) => void;
  removeEventMatcher: (index: number) => void;
  removeEventFilter: (eventIndex: number, filterIndex: number) => void;
  addEventFilter: (index: number) => void;
  removeEtSelector: (index: number, etSelectorIndex: number) => void;
  addEtSelector: (index: number) => void;
  addEventMatcher: () => void;
  addEventMatcherBySiteViewer: (newEventMatchers: EventMatcher[]) => void;
  addInclusionUrlBySiteViewer: (siteViewerUrl: string) => void;
};

type EventTrack = {
  action: string;
  events: any;
  url: string;
  frob: number;
};

type EventType = {
  name: string;
  tagName: string;
  attrHref?: string;
  attrId?: string;
  attrAction?: string;
  attrSrc?: string;
  attrClass?: string;
};

const EventContent: FC<Props> = ({
  goal,
  siteViewerUrl,
  setSiteViewerUrl,
  eventMatchers,
  setEventMatchers,
  removeEventMatcher,
  removeEventFilter,
  addEventFilter,
  removeEtSelector,
  addEtSelector,
  addEventMatcher,
  addEventMatcherBySiteViewer,
  addInclusionUrlBySiteViewer,
}) => {
  const config: Config = (window as any).__hugotconfig__;

  const [frob, setFrob] = useState<number>(0);
  const [openWindow, setOpenWindow] = useState<Window | null>(null);
  const encodedSiteUrl = encodeURIComponent(siteViewerUrl);
  const encodedGoalTitle = encodeURIComponent(goal.title);
  const encodedReturnUrl = encodeURIComponent(`https://app-dev2.pbcd.net/goals/${goal.id}/edit`);
  const url = `${config.EDITOR_URL}/ja/kz/?url=${encodedSiteUrl}&frob=${frob}&goal_id=${goal.id}&goal_title=${encodedGoalTitle}&is_mobile_web=false&device_type=all&track_text=false&mode=eventtrack&return_url=${encodedReturnUrl}`;

  const receiveMessage = (event: MessageEvent<string>) => {
    const eventTrack: EventTrack = JSON.parse(event.data);
    if (event && eventTrack && eventTrack.frob === frob) {
      const eventMatchers: EventMatcher[] = [];
      Object.values(eventTrack.events).map((event: unknown) => {
        const eventData: EventType = event as EventType;
        const conditions: Condition[] = [];
        if (eventData.tagName) {
          conditions.push({
            data: {
              args: eventData.tagName.toLowerCase(),
              method: 'tagName',
              type: 'string',
              comparator: 'EtEqual',
            },
          });
        }
        if (eventData.attrHref) {
          conditions.push({
            data: {
              args: eventData.attrHref,
              method: 'attrHref',
              type: 'string',
              comparator: 'EtEqual',
            },
          });
        }
        if (eventData.attrSrc) {
          conditions.push({
            data: {
              args: eventData.attrSrc,
              method: 'attrSrc',
              type: 'string',
              comparator: 'EtEqual',
            },
          });
        }
        if (eventData.attrId) {
          conditions.push({
            data: {
              args: eventData.attrId,
              method: 'attrId',
              type: 'string',
              comparator: 'EtEqual',
            },
          });
        }
        if (eventData.attrClass) {
          eventData.attrClass.split(' ').map((classValue) => {
            conditions.push({
              data: {
                args: classValue,
                method: 'attrClass',
                type: 'string',
                comparator: 'EtContain',
              },
            });
          });
        }

        eventMatchers.push({
          comparator: 'EtContain',
          event: 'click',
          type: 'EtEvent',
          name: eventData.name,
          conditions: conditions,
          etSelector: {
            data: {
              conditions: [],
            },
            type: 'EtSelector',
          },
        });
      });

      addInclusionUrlBySiteViewer(siteViewerUrl);
      addEventMatcherBySiteViewer(eventMatchers);

      closeWindow();
    }
  };

  const closeWindow = () => {
    openWindow?.close();
  };

  const handleWindow = () => {
    setFrob(Math.floor(Date.now() * Math.random()));
  };

  useEffect(() => {
    if (openWindow) {
      window.addEventListener('message', receiveMessage, false);
    }
  }, [openWindow]);

  useEffect(() => {
    if (frob !== 0) {
      setOpenWindow(window.open(url, 'site_viewer'));
    }
  }, [frob]);

  return (
    <Fragment>
      <Grid item xs={12}>
        <Subtitle>サイトビューアーで計測するイベントを設定する</Subtitle>
        <Description>
          以下にゴールページのURLを入力し「URLを開く」をクリックすると、ページをブラウジングしながらGUI上で計測するイベントを設定できます。
        </Description>
      </Grid>
      <Grid item xs={12}>
        <FlexWrapper>
          <StyledInputField
            value={siteViewerUrl}
            placeholder={'https://example.com/'}
            onChange={(e) => {
              setSiteViewerUrl(e.target.value);
            }}
            error={siteViewerUrl.length !== 0 && !checkUrl(siteViewerUrl)}
            width="100%"
          />
          <StyledUrlButton onClick={handleWindow} variant="outlined">
            URLを開く
          </StyledUrlButton>
        </FlexWrapper>
      </Grid>
      <ContentGrid item xs={12}>
        <Subtitle>計測するイベントの条件</Subtitle>
        <Description>
          イベントタイプおよびイベント対象要素を識別するためのHTMLタグ情報を設定してください（サイト画面ビューアーを利用すると自動入力できます）。設定された要素のいずれかがクリックされると、1コンバージョンとしてカウントされます。
        </Description>
      </ContentGrid>
      {eventMatchers.map((eventMatcher, eventIndex) => {
        if (eventMatcher.destroy) {
          return;
        }
        return (
          <EventGrid key={`${eventMatcher.id}-${eventIndex}`} container item xs={12}>
            <EventSubtitleGrid item xs={12}>
              <Subtitle>イベント定義の設定</Subtitle>
              <StyledIconButton
                onClick={() => {
                  removeEventMatcher(eventIndex);
                }}
              >
                <CloseIcon />
              </StyledIconButton>
            </EventSubtitleGrid>
            <EventInputGrid item xs={12}>
              <div>
                <EventTypeLabel>イベントタイプ</EventTypeLabel>
                <StyledSelect
                  value={eventMatcher.event}
                  onChange={(e) => {
                    eventMatcher.event = e.target.value as unknown as MatchEvent;
                    setEventMatchers(eventMatchers.slice());
                  }}
                  variant="outlined"
                >
                  <MenuItem value={'click'}>onclick</MenuItem>
                  <MenuItem value={'submit'}>onsubmit</MenuItem>
                  <MenuItem value={'change'}>onchange</MenuItem>
                </StyledSelect>
              </div>
              <EventInputWrapper>
                <EventTypeLabel>イベント名</EventTypeLabel>
                <StyledInputField
                  value={eventMatcher.name}
                  placeholder={'イベント名'}
                  onChange={(e) => {
                    eventMatcher.name = e.target.value;
                    setEventMatchers(eventMatchers.slice());
                  }}
                  width="100%"
                />
              </EventInputWrapper>
            </EventInputGrid>
            <StyledDivider />
            <FilterGrid item xs={12}>
              <Subtitle2>フィルタ条件</Subtitle2>
            </FilterGrid>
            {eventMatcher.conditions.map((condition, filterIndex) => {
              if (!condition) {
                return;
              }
              return (
                <EventInputGrid key={`filter-${filterIndex}`} item xs={12}>
                  <StyledSelect
                    key={filterIndex}
                    value={condition.data.method}
                    onChange={(e) => {
                      condition.data.method = e.target.value as unknown as FilterMethod;
                      if (
                        condition.data.method === 'attrId' ||
                        condition.data.method === 'attrAction' ||
                        condition.data.method === 'attrSrc' ||
                        condition.data.method === 'attrHref' ||
                        condition.data.method === 'tagName'
                      ) {
                        condition.data.comparator = 'EtEqual';
                      }
                      if (condition.data.method === 'attrClass') {
                        condition.data.comparator = 'EtContain';
                      }
                      if (condition.data.method === 'tagName') {
                        condition.data.args = 'a';
                      }
                      setEventMatchers(eventMatchers.slice());
                    }}
                    variant="outlined"
                  >
                    <MenuItem
                      disabled={eventMatcher.conditions.findIndex((a) => a.data.method === 'tagName') >= 0}
                      value={'tagName'}
                    >
                      タグ名
                    </MenuItem>
                    <MenuItem value={'attrClass'}>クラス名</MenuItem>
                    <MenuItem
                      disabled={eventMatcher.conditions.findIndex((a) => a.data.method === 'attrId') >= 0}
                      value={'attrId'}
                    >
                      要素ID
                    </MenuItem>
                    <MenuItem value={'attrHref'}>href属性</MenuItem>
                    <MenuItem value={'attrSrc'}>src属性</MenuItem>
                    <MenuItem value={'attrAction'}>action属性</MenuItem>
                  </StyledSelect>
                  <EventInputWrapper>
                    {condition.data.method !== 'tagName' ? (
                      <StyledInputField
                        value={condition.data.args}
                        onChange={(e) => {
                          condition.data.args = e.target.value;
                          setEventMatchers(eventMatchers.slice());
                        }}
                        width="100%"
                      />
                    ) : (
                      <StyledSelect
                        value={condition.data.args}
                        onChange={(e) => {
                          condition.data.args = e.target.value as string;
                          setEventMatchers(eventMatchers.slice());
                        }}
                        variant="outlined"
                      >
                        <MenuItem value={'a'}>a</MenuItem>
                        <MenuItem value={'img'}>img</MenuItem>
                        <MenuItem value={'div'}>div</MenuItem>
                        <MenuItem value={'span'}>span</MenuItem>
                        <MenuItem value={'form'}>form</MenuItem>
                        <MenuItem value={'select'}>select</MenuItem>
                        <MenuItem value={'input'}>input</MenuItem>
                        <MenuItem value={'button'}>button</MenuItem>
                        <MenuItem value={'option'}>option</MenuItem>
                        <MenuItem value={'textarea'}>textarea</MenuItem>
                        <MenuItem value={'label'}>label</MenuItem>
                        <MenuItem value={'table'}>table</MenuItem>
                        <MenuItem value={'th'}>th</MenuItem>
                        <MenuItem value={'tbody'}>tbody</MenuItem>
                        <MenuItem value={'tr'}>tr</MenuItem>
                        <MenuItem value={'td'}>td</MenuItem>
                        <MenuItem value={'ul'}>ul</MenuItem>
                        <MenuItem value={'ol'}>ol</MenuItem>
                        <MenuItem value={'li'}>li</MenuItem>
                        <MenuItem value={'dl'}>dl</MenuItem>
                        <MenuItem value={'dd'}>dd</MenuItem>
                        <MenuItem value={'dt'}>dt</MenuItem>
                        <MenuItem value={'p'}>p</MenuItem>
                        <MenuItem value={'i'}>i</MenuItem>
                        <MenuItem value={'bodu'}>body</MenuItem>
                        <MenuItem value={'strong'}>strong</MenuItem>
                        <MenuItem value={'em'}>em</MenuItem>
                        <MenuItem value={'area'}>area</MenuItem>
                        <MenuItem value={'h5'}>h5</MenuItem>
                        <MenuItem value={'h4'}>h4</MenuItem>
                        <MenuItem value={'h3'}>h3</MenuItem>
                        <MenuItem value={'h2'}>h2</MenuItem>
                        <MenuItem value={'h1'}>h1</MenuItem>
                        <MenuItem value={'font'}>font</MenuItem>
                        <MenuItem value={'small'}>small</MenuItem>
                        <MenuItem value={'b'}>b</MenuItem>
                        <MenuItem value={'u'}>u</MenuItem>
                        <MenuItem value={'canvas'}>canvas</MenuItem>
                        <MenuItem value={'figure'}>figure</MenuItem>
                        <MenuItem value={'nav'}>nav</MenuItem>
                        <MenuItem value={'address'}>address</MenuItem>
                        <MenuItem value={'center'}>center</MenuItem>
                        <MenuItem value={'header'}>header</MenuItem>
                        <MenuItem value={'footer'}>footer</MenuItem>
                        <MenuItem value={'section'}>section</MenuItem>
                        <MenuItem value={'main'}>main</MenuItem>
                        <MenuItem value={'article'}>article</MenuItem>
                        <MenuItem value={'aside'}>aside</MenuItem>
                        <MenuItem value={'video'}>video</MenuItem>
                        <MenuItem value={'iframe'}>iframe</MenuItem>
                        <MenuItem value={'time'}>time</MenuItem>
                        <MenuItem value={'figcaption'}>figcaption</MenuItem>
                        <MenuItem value={'pre'}>pre</MenuItem>
                        <MenuItem value={'code'}>code</MenuItem>
                        <MenuItem value={'sup'}>sup</MenuItem>
                        <MenuItem value={'object'}>object</MenuItem>
                        <MenuItem value={'caption'}>caption</MenuItem>
                        <MenuItem value={'map'}>map</MenuItem>
                        <MenuItem value={'fieldset'}>fieldset</MenuItem>
                        <MenuItem value={'nobr'}>nobr</MenuItem>
                        <MenuItem value={'tt'}>tt</MenuItem>
                      </StyledSelect>
                    )}
                  </EventInputWrapper>
                  {condition.data.method !== 'tagName' ? (
                    <UrlInputTypeSelect
                      value={getComparatorType(condition.data.comparator)}
                      onChange={(e) => {
                        condition.data.comparator = e.target.value as unknown as FilterType;
                        setEventMatchers(eventMatchers.slice());
                      }}
                      variant="outlined"
                    >
                      <MenuItem value={'EtContain'}>含む</MenuItem>
                      {condition.data.method !== 'attrClass' ? <MenuItem value={'EtEqual'}>等しい</MenuItem> : null}
                    </UrlInputTypeSelect>
                  ) : null}
                  <StyledIconButton
                    onClick={() => {
                      removeEventFilter(eventIndex, filterIndex);
                    }}
                  >
                    <CloseIcon />
                  </StyledIconButton>
                </EventInputGrid>
              );
            })}
            <Grid item xs={12}>
              <StyledContainedButton
                onClick={() => {
                  addEventFilter(eventIndex);
                }}
              >
                条件の追加
              </StyledContainedButton>
            </Grid>
            <SubtitleGrid item xs={12}>
              <Subtitle2>祖先要素によるフィルタ</Subtitle2>
            </SubtitleGrid>
            <FilterGrid item xs={12}>
              <SmallDescription>
                ページ内に多数のイベント対象要素がある場合に、条件に祖先要素を指定することで絞り込むことができます。
              </SmallDescription>
            </FilterGrid>
            {eventMatcher.etSelector.data.conditions.map((condition, filterIndex) => {
              if (!condition) {
                return;
              }
              return (
                <EventInputGrid key={`etselector-${filterIndex}`} item xs={12}>
                  <StyledSelect
                    key={filterIndex}
                    value={condition.data.method}
                    onChange={(e) => {
                      condition.data.method = e.target.value as unknown as FilterMethod;
                      if (condition.data.method === 'attrId') {
                        condition.type = 'EtEqual';
                      }
                      if (condition.data.method === 'attrClass') {
                        condition.type = 'EtContain';
                      }
                      if (condition.data.method === 'tagName') {
                        condition.data.args = 'a';
                        condition.type = 'EtEqual';
                      }
                      setEventMatchers(eventMatchers.slice());
                    }}
                    variant="outlined"
                  >
                    <MenuItem
                      disabled={
                        eventMatcher.etSelector.data.conditions.findIndex((a) => a.data.method === 'tagName') >= 0
                      }
                      value={'tagName'}
                    >
                      タグ名
                    </MenuItem>
                    <MenuItem value={'attrClass'}>クラス名</MenuItem>
                    <MenuItem
                      disabled={
                        eventMatcher.etSelector.data.conditions.findIndex((a) => a.data.method === 'attrId') >= 0
                      }
                      value={'attrId'}
                    >
                      要素ID
                    </MenuItem>
                  </StyledSelect>
                  <EventInputWrapper>
                    {condition.data.method !== 'tagName' ? (
                      <StyledInputField
                        value={condition.data.args}
                        onChange={(e) => {
                          condition.data.args = e.target.value;
                          setEventMatchers(eventMatchers.slice());
                        }}
                        width="100%"
                      />
                    ) : (
                      <StyledSelect
                        value={condition.data.args}
                        onChange={(e) => {
                          condition.data.args = e.target.value as string;
                          setEventMatchers(eventMatchers.slice());
                        }}
                        variant="outlined"
                      >
                        <MenuItem value={'a'}>a</MenuItem>
                        <MenuItem value={'img'}>img</MenuItem>
                        <MenuItem value={'div'}>div</MenuItem>
                        <MenuItem value={'span'}>span</MenuItem>
                        <MenuItem value={'form'}>form</MenuItem>
                        <MenuItem value={'select'}>select</MenuItem>
                        <MenuItem value={'input'}>input</MenuItem>
                        <MenuItem value={'button'}>button</MenuItem>
                        <MenuItem value={'option'}>option</MenuItem>
                        <MenuItem value={'textarea'}>textarea</MenuItem>
                        <MenuItem value={'label'}>label</MenuItem>
                        <MenuItem value={'table'}>table</MenuItem>
                        <MenuItem value={'th'}>th</MenuItem>
                        <MenuItem value={'tbody'}>tbody</MenuItem>
                        <MenuItem value={'tr'}>tr</MenuItem>
                        <MenuItem value={'td'}>td</MenuItem>
                        <MenuItem value={'ul'}>ul</MenuItem>
                        <MenuItem value={'ol'}>ol</MenuItem>
                        <MenuItem value={'li'}>li</MenuItem>
                        <MenuItem value={'dl'}>dl</MenuItem>
                        <MenuItem value={'dd'}>dd</MenuItem>
                        <MenuItem value={'dt'}>dt</MenuItem>
                        <MenuItem value={'p'}>p</MenuItem>
                        <MenuItem value={'i'}>i</MenuItem>
                        <MenuItem value={'bodu'}>body</MenuItem>
                        <MenuItem value={'strong'}>strong</MenuItem>
                        <MenuItem value={'em'}>em</MenuItem>
                        <MenuItem value={'area'}>area</MenuItem>
                        <MenuItem value={'h5'}>h5</MenuItem>
                        <MenuItem value={'h4'}>h4</MenuItem>
                        <MenuItem value={'h3'}>h3</MenuItem>
                        <MenuItem value={'h2'}>h2</MenuItem>
                        <MenuItem value={'h1'}>h1</MenuItem>
                        <MenuItem value={'font'}>font</MenuItem>
                        <MenuItem value={'small'}>small</MenuItem>
                        <MenuItem value={'b'}>b</MenuItem>
                        <MenuItem value={'u'}>u</MenuItem>
                        <MenuItem value={'canvas'}>canvas</MenuItem>
                        <MenuItem value={'figure'}>figure</MenuItem>
                        <MenuItem value={'nav'}>nav</MenuItem>
                        <MenuItem value={'address'}>address</MenuItem>
                        <MenuItem value={'center'}>center</MenuItem>
                        <MenuItem value={'header'}>header</MenuItem>
                        <MenuItem value={'footer'}>footer</MenuItem>
                        <MenuItem value={'section'}>section</MenuItem>
                        <MenuItem value={'main'}>main</MenuItem>
                        <MenuItem value={'article'}>article</MenuItem>
                        <MenuItem value={'aside'}>aside</MenuItem>
                        <MenuItem value={'video'}>video</MenuItem>
                        <MenuItem value={'iframe'}>iframe</MenuItem>
                        <MenuItem value={'time'}>time</MenuItem>
                        <MenuItem value={'figcaption'}>figcaption</MenuItem>
                        <MenuItem value={'pre'}>pre</MenuItem>
                        <MenuItem value={'code'}>code</MenuItem>
                        <MenuItem value={'sup'}>sup</MenuItem>
                        <MenuItem value={'object'}>object</MenuItem>
                        <MenuItem value={'caption'}>caption</MenuItem>
                        <MenuItem value={'map'}>map</MenuItem>
                        <MenuItem value={'fieldset'}>fieldset</MenuItem>
                        <MenuItem value={'nobr'}>nobr</MenuItem>
                        <MenuItem value={'tt'}>tt</MenuItem>
                      </StyledSelect>
                    )}
                  </EventInputWrapper>
                  {condition.data.method !== 'tagName' ? (
                    <UrlInputTypeSelect
                      value={getComparatorType(condition.type)}
                      onChange={(e) => {
                        condition.type = e.target.value as unknown as FilterType;
                        setEventMatchers(eventMatchers.slice());
                      }}
                      variant="outlined"
                    >
                      <MenuItem value={'EtContain'}>含む</MenuItem>
                      {condition.data.method !== 'attrClass' ? <MenuItem value={'EtEqual'}>等しい</MenuItem> : null}
                    </UrlInputTypeSelect>
                  ) : null}
                  <StyledIconButton
                    onClick={() => {
                      removeEtSelector(eventIndex, filterIndex);
                    }}
                  >
                    <CloseIcon />
                  </StyledIconButton>
                </EventInputGrid>
              );
            })}
            <Grid item xs={12}>
              <StyledContainedButton
                onClick={() => {
                  addEtSelector(eventIndex);
                }}
              >
                条件の追加
              </StyledContainedButton>
            </Grid>
          </EventGrid>
        );
      })}
      <ButtonGrid item xs={12}>
        <StyledButton
          variant="outlined"
          onClick={() => {
            addEventMatcher();
          }}
        >
          イベント定義の追加
        </StyledButton>
      </ButtonGrid>
    </Fragment>
  );
};

const Description = styled(Typography)`
  font-weight: 400;
  font-size: 14px;
  line-height: 160%;
  color: ${COLOR_TEXT_MAIN};
  margin-top: 8px;
  margin-bottom: 16px;
`;

const SmallDescription = styled(Typography)`
  font-weight: 400;
  font-size: 12px;
  line-height: 160%;
  color: ${COLOR_TEXT_MAIN};
`;

const Subtitle = styled(Typography)`
  font-weight: 700;
  font-size: 18px;
  line-height: 150%;
  display: flex;
  align-items: center;
  color: ${COLOR_TEXT_MAIN};
`;

const Subtitle2 = styled(Typography)`
  font-weight: 700;
  font-size: 14px;
  line-height: 150%;
  display: flex;
  align-items: center;
  color: ${COLOR_TEXT_MAIN};
`;

const FlexWrapper = styled('div')`
  display: flex;
`;

const StyledButton = styled(Button)({
  height: 40,
  '&.MuiButton-outlined': {
    fontWeight: 600,
    fontSize: 14,
    textAlign: 'center',
    color: '#4C566C',
    background: '#FFFFFF',
    border: '1.5px solid #E6E7E8',
    borderRadius: 4,
  },
  '&.MuiButton-contained': {
    fontWeight: 600,
    fontSize: 14,
    textAlign: 'center',
    color: '#FFFFFF',
    background: '#4C566C',
    border: '1.5px solid #4C566C',
    borderRadius: 4,
  },
});

const StyledUrlButton = styled(StyledButton)`
  height: 48px;
  width: 136px;
  margin-left: 16px;
`;

const ContentGrid = styled(Grid)`
  margin-top: 24px;
`;

const EventGrid = styled(Grid)`
  padding: 24px;
  background: #f5f6f7;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const EventSubtitleGrid = styled(Grid)`
  display: flex;
  justify-content: space-between;
`;

const EventInputGrid = styled(Grid)`
  display: flex;
  margin-bottom: 24px;
`;

const EventTypeLabel = styled(InputFieldLabel)`
  margin-bottom: 8px;
`;

const EventInputWrapper = styled('div')`
  width: 100%;
  margin-left: 16px;
`;

const FilterGrid = styled(Grid)`
  margin-bottom: 16px;
`;

const StyledDivider = styled(Divider)`
  margin-bottom: 24px;
  width: 100%;
`;

const UrlInputTypeSelect = styled(StyledSelect)`
  margin-left: 16px;
`;

const StyledContainedButton = styled(Button)`
  color: #ffffff;
  height: 32px;
  font-size: 12px;
  background: ${COLOR_TEXT_MAIN};
  font-weight: 800;
  border-color: ${COLOR_TEXT_MAIN};
  border-radius: 4px;
  padding: 12px 24px;
  text-transform: none;
  :hover {
    background: #707889;
    border-color: #707889;
  }
  :disabled {
    background: #e6e7e8;
    color: rgba(75, 85, 106, 0.24);
    border: rgba(75, 85, 106, 0.24);
  }
`;

const SubtitleGrid = styled(Grid)`
  margin-bottom: 4px;
  margin-top: 40px;
`;

export default EventContent;
